import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import type { MentionableEntity } from './RichTextField';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { NoteFormDialog } from './NoteFormDialog';
import { CreateProjectNoteDialog } from './CreateProjectNoteDialog';
import { NoteCard } from './NoteCard';
import { NotesBulkActions } from './NotesBulkActions';
import { SelectableNoteRow } from './SelectableNoteRow';
import { NotesEmptyState, notesListContainerClassName, NotesPwaShortcutHint } from './NotesListLayout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { AddItemButton } from './AddItemButton';
import { wouldExceedStorageQuota } from '../utils/storage-quota';
import { stripHtml, NOTES_CONTENT_MAX_LENGTH } from '../utils/note-utils';
import { downloadNoteAttachment } from '../utils/note-attachment-download';
import { useIsMobile } from '../hooks/use-mobile';
import { shouldShowHeaderCreateActions } from '../utils/header-create-actions';
import {
  fetchAssignableProjects,
  selectRecentAssignableProjects,
  type AssignableProject,
} from '../utils/recent-assignable-projects';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { notifyProjectNotesChanged } from '../utils/project-notes-events';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';
import type { ProjectNote } from '../types/project-note';

export type { ProjectNote } from '../types/project-note';

export const ProjectNotes: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { canEditProject } = useProjectAccess();
  const { t } = useLocale();
  const { showToast, showErrorToast, hideToast } = useToast();
  const isMobile = useIsMobile();
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const createQueryRequested = searchParams.get('create') === '1';
  const noteIdParam = searchParams.get('noteId');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editAttachmentResourceId, setEditAttachmentResourceId] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [projects, setProjects] = useState<AssignableProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [moveNoteIds, setMoveNoteIds] = useState<string[]>([]);
  const [bulkActing, setBulkActing] = useState(false);
  const getVisibleNoteIdsRef = useRef<() => string[]>(() => notes.map((note) => note.id));
  getVisibleNoteIdsRef.current = () => notes.map((note) => note.id);
  const {
    selectedIds: selectedNoteIds,
    setRowSelection,
    selectRangeTo,
    toggleRowSelected,
    clearSelection,
    setAnchorRowId,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleNoteIdsRef.current(),
  });

  const handleSelectAllChange = (checked: boolean): void => {
    const visibleIds = getVisibleNoteIdsRef.current();
    if (!checked) {
      clearSelection();
      return;
    }
    const next: Record<string, boolean> = {};
    for (const id of visibleIds) {
      next[id] = true;
    }
    setRowSelection(next);
    setAnchorRowId(visibleIds.length > 0 ? visibleIds[0] : null);
  };

  const editingNote = editNoteId ? notes.find((n) => n.id === editNoteId) : null;
  const otherProjects = projects.filter((p) => p.id !== projectId);
  const recentProjects = selectRecentAssignableProjects(otherProjects);
  const noteFormOpen = Boolean(editingNote);
  const moveDialogOpen = moveNoteIds.length > 0;

  useEffect(() => {
    if (canEditProject && createQueryRequested) {
      setIsCreateOpen(true);
    }
  }, [canEditProject, createQueryRequested]);

  useEffect(() => {
    if ((!isCreateOpen && createQueryRequested) || (!canEditProject && createQueryRequested)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('create');
          return next;
        },
        { replace: true }
      );
    }
  }, [isCreateOpen, createQueryRequested, canEditProject, setSearchParams]);

  useEffect(() => {
    const handleNoteCreated = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string; note?: ProjectNote }>).detail;
      if (detail?.projectId !== projectId || !detail.note) {
        return;
      }
      setNotes((prev) => {
        if (prev.some((note) => note.id === detail.note!.id)) {
          return prev;
        }
        return [detail.note!, ...prev];
      });
    };
    window.addEventListener('note-created', handleNoteCreated);
    return () => window.removeEventListener('note-created', handleNoteCreated);
  }, [projectId]);

  useEffect(() => {
    if (!noteIdParam || loading) {
      return;
    }
    const note = notes.find((entry) => entry.id === noteIdParam);
    if (note) {
      setEditContent(note.content);
      setEditAttachmentResourceId(note.attachmentResourceId);
      setEditFile(null);
      setEditNoteId(note.id);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('noteId');
        return next;
      },
      { replace: true }
    );
  }, [noteIdParam, loading, notes, setSearchParams]);

  const loadAssignableProjects = useCallback(async (): Promise<void> => {
    if (!token) return;
    setProjectsLoading(true);
    try {
      const assignable = await fetchAssignableProjects(token);
      setProjects(assignable);
    } catch {
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (editingNote) {
      setSelectedProjectId(null);
    }
  }, [editingNote]);

  useEffect(() => {
    if (moveDialogOpen || noteFormOpen) {
      void loadAssignableProjects();
    }
  }, [moveDialogOpen, noteFormOpen, loadAssignableProjects]);

  useEffect(() => {
    if (!projectId || !token) return;
    const fetchNotes = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setNotes(data.notes || []);
        } else {
          setNotes([]);
        }
      } catch {
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();

    const handleNoteRestored = (event: Event): void => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId === projectId) {
        void fetchNotes();
      }
    };
    window.addEventListener('note-restored', handleNoteRestored);
    return () => window.removeEventListener('note-restored', handleNoteRestored);
  }, [projectId, token]);

  useEffect(() => {
    if (!projectId || !token) return;
    const fetchMentionable = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    fetchMentionable();
  }, [projectId, token]);

  useEffect(() => {
    if (editingNote) {
      setEditContent(editingNote.content);
      setEditAttachmentResourceId(editingNote.attachmentResourceId);
      setEditFile(null);
    }
  }, [editingNote]);

  const editContentLength = stripHtml(editContent).length;
  const editContentOk = editContentLength <= NOTES_CONTENT_MAX_LENGTH;

  const uploadAttachmentAsResource = async (file: File): Promise<string | null> => {
    if (!token || !projectId) return null;
    const exceedsQuota = await wouldExceedStorageQuota(token, file.size);
    if (exceedsQuota) {
      window.dispatchEvent(new CustomEvent('storage-limit-reached'));
      return null;
    }
    setUploadingAttachment(true);
    try {
      const presignedRes = await fetch(`${API_BASE}/api/projects/${projectId}/resources/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
        }),
      });
      if (!presignedRes.ok) {
        const err = await presignedRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to get upload URL');
      }
      const { presignedUrl, s3Key } = await presignedRes.json();
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const saveRes = await fetch(`${API_BASE}/api/projects/${projectId}/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
          s3Key,
          fromNote: true,
        }),
      });
      if (!saveRes.ok) {
        const err = await saveRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save resource');
      }
      const saveData = await saveRes.json();
      return saveData.id;
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Upload failed');
      return null;
    } finally {
      setUploadingAttachment(false);
    }
  };

  const showMovedToast = (targetProjectId: string, projectTitle: string): void => {
    const message = (t('note_moved_to_project_toast') || 'The note has been moved to {projectTitle} project.').replace(
      '{projectTitle}',
      projectTitle
    );
    showToast(
      <>
        {message}
        {' '}
        <Link
          to={`/projects/${targetProjectId}/notes`}
          style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 500 }}
          onClick={() => hideToast()}
        >
          {t('open_notes') || 'Open notes'}
        </Link>
      </>
    );
  };

  const moveNotesToProject = async (
    noteIds: string[],
    targetProjectId: string,
    projectTitle: string
  ): Promise<void> => {
    if (!token || noteIds.length === 0) return;
    for (const noteId of noteIds) {
      const res = await fetch(`${API_BASE}/api/notes/${noteId}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId: targetProjectId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to move note');
      }
      await readJsonWithOnboarding(res);
    }
    setNotes((prev) => prev.filter((n) => !noteIds.includes(n.id)));
    setMoveNoteIds([]);
    setEditNoteId(null);
    setSelectedProjectId(null);
    clearSelection();
    if (projectId) {
      notifyProjectNotesChanged(projectId);
    }
    notifyProjectNotesChanged(targetProjectId);
    if (noteIds.length === 1) {
      showMovedToast(targetProjectId, projectTitle);
    } else {
      const message = (t('notes_moved_to_project_toast') || '{count} notes have been moved to {projectTitle} project.')
        .replace('{count}', String(noteIds.length))
        .replace('{projectTitle}', projectTitle);
      showToast(
        <>
          {message}
          {' '}
          <Link
            to={`/projects/${targetProjectId}/notes`}
            style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 500 }}
            onClick={() => hideToast()}
          >
            {t('open_notes') || 'Open notes'}
          </Link>
        </>
      );
    }
  };

  const handleEditSave = async (): Promise<void> => {
    if (!token || !editingNote || !editContentOk) return;
    let attachmentResourceId: string | null = editAttachmentResourceId;
    if (editFile) {
      const resourceId = await uploadAttachmentAsResource(editFile);
      if (resourceId) attachmentResourceId = resourceId;
      else return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/notes/${editingNote.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: editContent,
          attachmentResourceId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update note');
      }
      const data = await res.json();
      if (selectedProjectId && selectedProjectId !== projectId) {
        const project = projects.find((p) => p.id === selectedProjectId);
        await moveNotesToProject(
          [editingNote.id],
          selectedProjectId,
          project?.title || t('project_title') || 'Project'
        );
        return;
      }
      setNotes((prev) => prev.map((n) => (n.id === editingNote.id ? data.note : n)));
      setEditNoteId(null);
      setSelectedProjectId(null);
      showToast(t('note_updated') || 'Note updated');
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (targetProjectId: string, projectTitle: string): Promise<void> => {
    setBulkActing(true);
    try {
      await moveNotesToProject(moveNoteIds, targetProjectId, projectTitle);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to move note');
    } finally {
      setBulkActing(false);
    }
  };

  const deleteNotes = async (noteIds: string[]): Promise<void> => {
    if (!token || noteIds.length === 0) return;
    for (const noteId of noteIds) {
      const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete note');
    }
    setNotes((prev) => prev.filter((n) => !noteIds.includes(n.id)));
    if (editNoteId && noteIds.includes(editNoteId)) {
      setEditNoteId(null);
    }
    clearSelection();
    showToast(
      <>
        {t('note_moved_to') || 'Note moved to '}
        <Link
          to={`/projects/${projectId}/trash`}
          style={{
            color: '#ffffff',
            textDecoration: 'underline',
            fontWeight: 500,
          }}
        >
          {t('trash')}
        </Link>
      </>
    );
    for (const noteId of noteIds) {
      window.dispatchEvent(new CustomEvent('note-deleted', {
        detail: { noteId, projectId },
      }));
    }
    if (projectId) {
      notifyProjectNotesChanged(projectId);
    }
  };

  const handleDelete = async (noteId: string): Promise<void> => {
    try {
      await deleteNotes([noteId]);
    } catch {
      showErrorToast(t('failed_to_delete_note') || 'Failed to move note to trash');
    }
  };

  const handleBulkMoveToTrash = async (): Promise<void> => {
    if (selectedNoteIds.length === 0 || bulkActing) return;
    setBulkActing(true);
    try {
      await deleteNotes(selectedNoteIds);
    } catch {
      showErrorToast(t('failed_to_delete_note') || 'Failed to move note to trash');
    } finally {
      setBulkActing(false);
    }
  };

  const handleDownloadAttachment = (note: ProjectNote): void => {
    if (!token || !projectId || !note.attachmentResourceId || !note.attachmentFileName) return;
    downloadNoteAttachment(
      API_BASE,
      token,
      { type: 'project', projectId, resourceId: note.attachmentResourceId },
      (msg) => showErrorToast(msg)
    );
  };

  const openCreateNote = (): void => {
    setIsCreateOpen(true);
  };

  const notesActions = canEditProject && shouldShowHeaderCreateActions(notes.length > 0, isMobile) ? (
    <AddItemButton
      onClick={openCreateNote}
      disabled={!token}
      label={t('create_note') || 'Create note'}
    />
  ) : undefined;

  return (
    <>
      <ProjectPanelLayout
        title={t('Notes')}
        titleIcon={PROJECT_SECTION_ICONS.notes}
        actions={notesActions}
        loading={loading}
      >
        <div className={notesListContainerClassName}>
          {notes.length === 0 ? (
            <NotesEmptyState
              canEdit={Boolean(canEditProject)}
              onAdd={openCreateNote}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {canEditProject ? (
                <NotesBulkActions
                  selectedCount={selectedNoteIds.length}
                  totalCount={notes.length}
                  disabled={bulkActing}
                  onSelectAllChange={handleSelectAllChange}
                  onMoveToProject={() => setMoveNoteIds([...selectedNoteIds])}
                  onMoveToTrash={() => {
                    void handleBulkMoveToTrash();
                  }}
                />
              ) : null}
              {notes.map((note) => {
                const noteCard = (
                  <NoteCard
                    note={note}
                    selected={canEditProject && selectedNoteIds.includes(note.id)}
                    onEditClick={() => {
                      setEditContent(note.content);
                      setEditAttachmentResourceId(note.attachmentResourceId);
                      setEditFile(null);
                      setEditNoteId(note.id);
                    }}
                    onDownloadAttachment={
                      note.attachmentFileName
                        ? () => handleDownloadAttachment(note)
                        : undefined
                    }
                    onMoveToProject={
                      canEditProject
                        ? () => setMoveNoteIds([note.id])
                        : undefined
                    }
                    onMoveToTrash={
                      canEditProject
                        ? () => {
                            void handleDelete(note.id);
                          }
                        : undefined
                    }
                  />
                );
                if (!canEditProject) {
                  return <React.Fragment key={note.id}>{noteCard}</React.Fragment>;
                }
                return (
                  <SelectableNoteRow
                    key={note.id}
                    noteId={note.id}
                    selected={selectedNoteIds.includes(note.id)}
                    ariaLabel={t('select_note')}
                    onSelectRange={selectRangeTo}
                    onToggle={toggleRowSelected}
                  >
                    {noteCard}
                  </SelectableNoteRow>
                );
              })}
              <NotesPwaShortcutHint />
            </div>
          )}
        </div>
      </ProjectPanelLayout>

      {canEditProject && projectId && (
        <CreateProjectNoteDialog
          projectId={projectId}
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          onCreated={(note) => {
            setNotes((prev) => {
              if (prev.some((existing) => existing.id === note.id)) {
                return prev;
              }
              return [note, ...prev];
            });
          }}
        />
      )}

      {editingNote && (
        <NoteFormDialog
          open={!!editingNote}
          onOpenChange={(open) => {
            if (!open) {
              setEditNoteId(null);
              setSelectedProjectId(null);
            }
          }}
          mode="edit"
          fieldKey={editingNote.id}
          content={editContent}
          onContentChange={setEditContent}
          file={editFile}
          onFileChange={setEditFile}
          existingAttachmentFileName={editingNote.attachmentFileName}
          onDownloadExistingAttachment={
            editingNote.attachmentFileName
              ? () => handleDownloadAttachment(editingNote)
              : undefined
          }
          mentionableEntities={mentionableEntities}
          contentLength={editContentLength}
          maxLength={NOTES_CONTENT_MAX_LENGTH}
          contentOk={editContentOk}
          saving={saving}
          uploadingAttachment={uploadingAttachment}
          createdAt={editingNote.createdAt}
          readOnly={!canEditProject}
          projectOptions={canEditProject ? recentProjects : []}
          selectedProjectId={selectedProjectId}
          onSelectedProjectIdChange={canEditProject ? setSelectedProjectId : undefined}
          onDelete={
            canEditProject
              ? () => {
                  void handleDelete(editingNote.id);
                  setEditNoteId(null);
                  setSelectedProjectId(null);
                }
              : undefined
          }
          onMoveToProject={
            canEditProject
              ? () => {
                  setEditNoteId(null);
                  setSelectedProjectId(null);
                  setMoveNoteIds([editingNote.id]);
                }
              : undefined
          }
          onSave={() => {
            void handleEditSave();
          }}
          onCancel={() => {
            setEditNoteId(null);
            setSelectedProjectId(null);
          }}
        />
      )}

      <Dialog
        open={moveDialogOpen}
        onOpenChange={(open) => {
          if (!open) setMoveNoteIds([]);
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t('move_to_project') || 'Move to project'}</DialogTitle>
          </DialogHeader>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {projectsLoading ? (
              <div>{t('loading')}</div>
            ) : otherProjects.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>{t('no_projects') || 'No projects'}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {otherProjects.map((p) => (
                    <Button
                      key={p.id}
                      variant="ghost"
                      className="justify-start"
                      disabled={bulkActing}
                      onClick={() => {
                        void handleMove(p.id, p.title);
                      }}
                    >
                      {p.title}
                    </Button>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
